import React, { useEffect, useState } from 'react';

interface StatsBannerSectionProps {
  stats?: {
    summary?: {
      totalBooks?: number | string;
      totalScans?: number | string;
      totalApproved?: number | string;
    };
    rolesCount?: {
      penerbit?: number | string;
      penulis?: number | string;
    };
  };
}

const AnimatedCounter: React.FC<{ value: number | string | undefined; fallback: string }> = ({ value, fallback }) => {
  const [displayValue, setDisplayValue] = useState<string>('0');

  useEffect(() => {
    if (value === undefined || value === null) {
      setDisplayValue(fallback);
      return;
    }

    const rawString = String(value);
    const numericMatch = rawString.match(/\d+/);
    if (!numericMatch) {
      setDisplayValue(rawString || fallback);
      return;
    }

    const targetNumber = parseInt(numericMatch[0], 10);
    const nonNumericSuffix = rawString.replace(/\d+/, '');

    if (targetNumber <= 0) {
      setDisplayValue(rawString);
      return;
    }

    let startTimestamp: number | null = null;
    const duration = 1200; // Animation duration in ms (1.2s for crisp feeling)

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // Easing out quadratic function
      const easeProgress = progress * (2 - progress);
      const currentNumber = Math.floor(easeProgress * targetNumber);
      
      setDisplayValue(`${currentNumber}${nonNumericSuffix}`);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setDisplayValue(rawString);
      }
    };

    const animFrame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(animFrame);
    };
  }, [value, fallback]);

  return <span>{displayValue}</span>;
};

export const StatsBannerSection: React.FC<StatsBannerSectionProps> = ({ stats }) => {
  const totalPenulis = Number(stats?.rolesCount?.penulis || 0);
  const totalPenerbit = Number(stats?.rolesCount?.penerbit || 0);
  const totalDirectory = totalPenulis + totalPenerbit;

  return (
    <section className="w-full max-w-7xl 2xl:max-w-[1536px] 3xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10">
      <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 2xl:p-10 shadow-xl relative overflow-hidden border border-slate-800">
        <div className="relative z-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 2xl:gap-8 text-center">
          <div>
            <div className="text-2xl sm:text-4xl 2xl:text-5xl font-extrabold text-white tracking-tight">
              <AnimatedCounter value={stats?.summary?.totalBooks} fallback="5+" />
            </div>
            <p className="text-xs sm:text-sm 2xl:text-base text-slate-300 mt-1 font-medium">Buku Terdaftar</p>
          </div>
          <div>
            <div className="text-2xl sm:text-4xl 2xl:text-5xl font-extrabold text-indigo-400 tracking-tight">
              <AnimatedCounter value={stats?.summary?.totalScans} fallback="12+" />
            </div>
            <p className="text-xs sm:text-sm 2xl:text-base text-slate-300 mt-1 font-medium">Total Pemindaian</p>
          </div>
          <div>
            <div className="text-2xl sm:text-4xl 2xl:text-5xl font-extrabold text-emerald-400 tracking-tight">
              <AnimatedCounter value={stats?.summary?.totalApproved} fallback="3+" />
            </div>
            <p className="text-xs sm:text-sm 2xl:text-base text-slate-300 mt-1 font-medium">UIBN Terverifikasi</p>
          </div>
          {/* New Public Directory Counter matching others exactly */}
          <div>
            <div className="text-2xl sm:text-4xl 2xl:text-5xl font-extrabold text-cyan-400 tracking-tight">
              <AnimatedCounter value={totalDirectory > 0 ? `${totalDirectory}+` : undefined} fallback="3+" />
            </div>
            <p className="text-xs sm:text-sm 2xl:text-base text-slate-300 mt-1 font-medium">Direktori Publik</p>
          </div>
          <div>
            <div className="text-2xl sm:text-4xl 2xl:text-5xl font-extrabold text-amber-400 tracking-tight">
              <AnimatedCounter value={stats?.rolesCount?.penerbit} fallback="1+" />
            </div>
            <p className="text-xs sm:text-sm 2xl:text-base text-slate-300 mt-1 font-medium">Penerbit Aktif</p>
          </div>
          <div>
            <div className="text-2xl sm:text-4xl 2xl:text-5xl font-extrabold text-rose-400 tracking-tight">
              <AnimatedCounter value={stats?.rolesCount?.penulis} fallback="2+" />
            </div>
            <p className="text-xs sm:text-sm 2xl:text-base text-slate-300 mt-1 font-medium">Penulis Aktif</p>
          </div>
        </div>
      </div>
    </section>
  );
};
